import VariantPicker from "@theme/variant-picker";
import { Component } from "@theme/component";
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent } from "@theme/events";
import { morph } from "@theme/morph";

export class ProductCard extends Component {
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );
    return checkedInput?.dataset.variantId || null;
  }

  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
    requestAnimationFrame(() => {
      this.#updateVariantImages();
    });
  };

  #updateProductUrl(event) {
    // Combined listing: update from newProduct data (works with cache)
    if (event.detail.data.newProduct?.url) {
      const productUrl = event.detail.data.newProduct.url;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      if (productCardLink) productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) cardGalleryLink.href = productUrl;
      if (productTitleLink instanceof HTMLAnchorElement) productTitleLink.href = productUrl;

      const featuredMediaUrl = event.detail.resource?.featured_media?.preview_image?.src;
      if (featuredMediaUrl && this.closest("product-card-link")) {
        this.closest("product-card-link")?.setAttribute("data-featured-media-url", featuredMediaUrl);
      }
      return;
    }

    // Fallback: get URL from HTML (cache miss)
    if (!event.detail.data.html) return;

    const anchorElement = event.detail.data.html.querySelector("product-card a");
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector("product-card-link")
      ?.getAttribute("data-featured-media-url");

    if (featuredMediaUrl && this.closest("product-card-link")) {
      this.closest("product-card-link")?.setAttribute("data-featured-media-url", featuredMediaUrl);
    }

    if (anchorElement instanceof HTMLAnchorElement) {
      if (anchorElement.getAttribute("href")?.trim() === "") return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      if (productCardLink) productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) cardGalleryLink.href = productUrl;
      if (productTitleLink instanceof HTMLAnchorElement) productTitleLink.href = productUrl;
    }
  }

  #updateVariantImages(event) {
    const mainImage = this.refs.cardGalleryLink?.querySelector(".product-card-main-image");
    if (!mainImage) return;

    const selectedSwatch = this.querySelector('input[type="radio"]:checked');
    if (!selectedSwatch) return;

    // Combined listing: check product match
    const swatchProductId = this.variantPicker?.dataset.productId;
    const currentProductId = this.dataset.productId || this.closest("product-card")?.dataset.productId;
    if (swatchProductId && currentProductId && swatchProductId !== currentProductId) {
      return;
    }

    const selectedLabel = selectedSwatch.closest("label");
    if (!selectedLabel) return;

    // Skip if swatch is hidden
    const swatchItem = selectedLabel.closest(".variant-option__swatch");
    if (swatchItem?.classList.contains("hidden")) return;

    let selectedImage = selectedLabel.querySelector(".media__image");

    // Unavailable variant: if swatch doesn't match event variant, try HTML first
    const swatchVariantId = selectedSwatch.dataset.variantId;
    const eventVariantId = event?.detail.resource?.id;
    if (eventVariantId && swatchVariantId && swatchVariantId !== eventVariantId && event?.detail.data.html) {
      if (this.#updateImageFromHTML(mainImage, event.detail.data.html)) {
        return;
      }
    }

    // Wait for image if not found yet
    if (!selectedImage) {
      const mediaId = selectedLabel.dataset.mediaId || selectedSwatch.dataset.optionMediaId;
      if (mediaId) {
        this.#waitForImageAndUpdate(mainImage, selectedLabel);
      }
      return;
    }

    // Apply image immediately if src exists
    if (selectedImage.src || selectedImage.currentSrc) {
      this.#applyImageToMain(mainImage, selectedImage);
      if (!selectedImage.complete) {
        selectedImage.addEventListener(
          "load",
          () => {
            if (selectedImage.srcset && mainImage.srcset !== selectedImage.srcset) {
              mainImage.srcset = selectedImage.srcset;
            }
          },
          { once: true }
        );
      }
    } else {
      selectedImage.addEventListener(
        "load",
        () => {
          this.#applyImageToMain(mainImage, selectedImage);
        },
        { once: true }
      );
    }
  }

  #waitForImageAndUpdate(mainImage, label) {
    const checkImage = () => {
      const img = label.querySelector(".media__image");
      if (img && (img.complete || img.naturalWidth > 0)) {
        this.#applyImageToMain(mainImage, img);
        return true;
      }
      return false;
    };

    if (checkImage()) return;

    requestAnimationFrame(() => {
      if (checkImage()) return;
      const img = label.querySelector(".media__image");
      if (img) {
        img.addEventListener("load", () => this.#applyImageToMain(mainImage, img), { once: true });
      }
    });
  }

  #updateImageFromHTML(mainImage, html) {
    const newCard = html.querySelector(`product-card[data-product-id="${this.dataset.productId}"]`);
    if (!newCard) return false;

    const newMainImage = newCard.querySelector(".product-card-main-image");
    if (!newMainImage) return false;

    let updated = false;
    if (newMainImage.srcset && mainImage.srcset !== newMainImage.srcset) {
      mainImage.srcset = newMainImage.srcset;
      updated = true;
    }
    if (newMainImage.src && mainImage.src !== newMainImage.src) {
      mainImage.src = newMainImage.src;
      updated = true;
    }
    return updated;
  }

  #applyImageToMain(mainImage, sourceImage) {
    if (!mainImage || !sourceImage) return;

    const newSrcset = sourceImage.srcset || "";
    const newSrc = sourceImage.currentSrc || sourceImage.src;

    if (newSrcset && mainImage.srcset !== newSrcset) {
      mainImage.srcset = newSrcset;
    }
    if (newSrc && mainImage.src !== newSrc) {
      mainImage.src = newSrc;
    }
  }

  #handleVariantUpdate = (event) => {
    event.stopPropagation();

    this.#updateProductUrl(event);

    if (event.target !== this.variantPicker && event.detail.data.html) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    // Restore swatches expanded state after morph
    if (this.variantPicker instanceof SwatchesVariantPicker && event.detail.data.html) {
      const expandedKey = this.variantPicker.getStorageKey();
      if (sessionStorage.getItem(expandedKey) === "true") {
        requestAnimationFrame(() => {
          this.variantPicker.restoreExpandedState();
        });
      }
    }

    // Update images: immediate for cache, deferred for cache miss
    if (event.detail.data.isOptimistic || event.detail.data.isBackgroundSync) {
      this.#updateVariantImages(event);
    } else {
      requestAnimationFrame(() => {
        this.#updateVariantImages(event);
      });
    }
  };

  get variantPicker() {
    return this.querySelector("swatches-variant-picker");
  }
}

if (!customElements.get("product-card")) {
  customElements.define("product-card", ProductCard);
}

class SwatchesVariantPicker extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest("product-card");

    // Clear storage on page load (not on morph) - detect by checking if component is new
    if (!this.dataset.initialized) {
      const expandedKey = this.getStorageKey();
      sessionStorage.removeItem(expandedKey);
      this.dataset.initialized = "true";
    }
  }

  updatedCallback() {
    super.updatedCallback?.();

    // Restore expanded state after component update (morph)
    const expandedKey = this.getStorageKey();
    const shouldExpand = sessionStorage.getItem(expandedKey) === "true";

    if (shouldExpand) {
      this.restoreExpandedState();
    }
  }

  getStorageKey() {
    const productId = this.dataset.productId || "unknown";
    return `swatches-expanded-${productId}`;
  }

  restoreExpandedState() {
    const hiddenSwatches = this.querySelectorAll(".variant-option__swatch.hidden");
    const showMoreButton = this.querySelector(".variant-option__swatch--show-more");

    hiddenSwatches.forEach((swatch) => swatch.classList.remove("hidden"));
    if (showMoreButton) showMoreButton.classList.add("hidden");
  }

  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes("-swatch");
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || "0");
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // Unavailable variant with alternatives: load available variant instead
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      event.stopPropagation();
      this.updateSelectedOption(clickedSwatch);

      const productUrl = this.dataset.productUrl?.split("?")[0];
      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set("variant", firstAvailableVariantId);
      url.searchParams.set("section_id", "section-rendering-product-card");

      const fetchToken = this.beginVariantFetch();
      this.fetchUpdatedSection(url.href, false, false, fetchToken);
      return;
    }

    super.variantChanged(event);
  }

  showAllSwatches(event) {
    event?.preventDefault();
    const hiddenSwatches = this.querySelectorAll(".variant-option__swatch.hidden");
    if (hiddenSwatches.length === 0) return;

    hiddenSwatches.forEach((swatch) => swatch.classList.remove("hidden"));
    this.querySelector(".variant-option__swatch--show-more")?.classList.add("hidden");
    sessionStorage.setItem(this.getStorageKey(), "true");
  }
}
if (!customElements.get("swatches-variant-picker")) {
  customElements.define("swatches-variant-picker", SwatchesVariantPicker);
}

/**
 * Navigation swatches sourced from the `custom.colour_variants` metafield.
 * Handles two contexts:
 *  - card: intercept the swatch click and update the card's image + links in place.
 *  - pdp:  swatches are plain links; on mount, measure which wrap to a second row,
 *          hide them, and reveal a "+N" overflow button.
 */
class ColourVariantSwatches extends Component {
  /** Whether the user has clicked "+N" to expand all swatches. */
  #isExpanded = false;

  /** ResizeObserver that re-measures single-row overflow on container resize (PDP and card). */
  #resizeObserver = null;
  /** Last observed width; used to ignore height-only changes we cause by hiding swatches. */
  #lastWidth = -1;
  /** Pending rAF id, so resize bursts coalesce into a single re-measure. */
  #rafId = 0;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("click", this.#handleClick);

    // PDP and card: the observer's initial callback performs the first measure after
    // layout, and re-measures on width changes thereafter.
    if (this.dataset.context === "pdp" || this.dataset.context === "card") {
      this.#startResizeObserver();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    if (this.#rafId) cancelAnimationFrame(this.#rafId);
    ColourVariantSwatches.#pendingCards.delete(this);
  }

  #handleClick = (event) => {
    const swatch = event.target.closest("a[data-product-url]");
    if (!swatch) return; // Overflow button is handled via on:click="/showAllSwatches".

    const card = this.closest("product-card");
    if (card) {
      // Card: intercept navigation and update the card in place.
      event.preventDefault();
      this.#setActiveSwatch(swatch);
      this.#updateCard(card, swatch);
    }
    // PDP: allow default <a> navigation.
  };

  #setActiveSwatch(active) {
    this.querySelectorAll("a[data-product-url]").forEach((a) => a.removeAttribute("aria-current"));
    active.setAttribute("aria-current", "true");
  }

  #updateCard(card, swatch) {
    const newUrl = swatch.dataset.productUrl;
    const newSrc = swatch.dataset.featuredImageSrc;

    ["productCardLink", "cardGalleryLink", "productTitleLink"].forEach((ref) => {
      const el = card.refs?.[ref];
      if (el instanceof HTMLAnchorElement && newUrl) el.href = newUrl;
    });

    const mainImage = card.refs?.cardGalleryLink?.querySelector(".product-card-main-image");
    if (mainImage && newSrc) {
      mainImage.src = newSrc;
      mainImage.srcset = "";
    }
  }

  /**
   * PDP: keep the swatches plus the "+N" badge on a single row. The badge is exactly
   * one swatch-slot wide, so once we know how many swatches fit on the first row we
   * keep (that count - 1) and let the badge take the freed slot — no iterative
   * re-measuring. Reads and writes are kept in separate phases to avoid layout thrash.
   */
  #initPdpOverflow() {
    const swatches = [
      ...this.querySelectorAll(".variant-option__swatch:not(.variant-option__swatch--show-more)"),
    ];
    const overflowItem = this.querySelector(".variant-option__swatch--show-more");
    const overflowCount = overflowItem?.querySelector(".colour-swatch-overflow__count");
    if (swatches.length === 0 || !overflowItem || !overflowCount) return;

    // WRITE: reset to a known state (all swatches shown, badge hidden), and lift the pre-measure single-row
    // clamp (theme.css) so the rows below can be measured. All in one frame, so the clamp-off state never paints.
    this.dataset.measured = "true";
    swatches.forEach((swatch) => swatch.classList.remove("hidden"));
    overflowItem.classList.add("hidden");

    // READ: count how many swatches sit on the first row. Flex-wrap fills row 1 in
    // DOM order, so the first swatch on a lower row ends the count.
    const firstRowTop = Math.round(swatches[0].getBoundingClientRect().top);
    let firstRowCount = 0;
    for (const swatch of swatches) {
      if (Math.round(swatch.getBoundingClientRect().top) !== firstRowTop) break;
      firstRowCount++;
    }

    // All swatches already fit — no overflow.
    if (firstRowCount === swatches.length) return;

    // WRITE: keep (firstRowCount - 1) swatches so the badge fits in the freed slot.
    const visibleCount = firstRowCount - 1;
    swatches.forEach((swatch, i) => swatch.classList.toggle("hidden", i >= visibleCount));
    overflowCount.textContent = `+${swatches.length - visibleCount}`;
    overflowItem.classList.remove("hidden");
  }

  /**
   * Card: dynamically fit colour-variant swatches + the "+N" overflow indicator on a
   * single flex row, handling any card width and responsive layout.
   *
   * On cards only `swatch_limit` swatches are rendered; the overflow `<li>` is always
   * in the DOM (class `variant-option__swatch--overflow-item`) and starts hidden when
   * the static count says no overflow is needed. Each card goes through:
   *
   *  1. WRITE (#resetCardOverflow) — resets to a known state.
   *     - When unrendered swatches exist (total > rendered), the overflow indicator
   *       must always be shown, so it's revealed immediately — it participates in the
   *       layout measurement.
   *     - Otherwise the indicator is hidden so the measurement is clean.
   *  2. READ (#measureCardOverflow) — BoundingClientRect measurement.
   *  3. WRITE (#applyCardOverflow) — hides overflow swatches, sets the correct "+N"
   *     count, reveals the indicator when needed.
   *
   * Cards are batched (#queueCardOverflow): every queued card resets, then every card is
   * measured, then every card is updated. Run one card at a time, each card's reset would
   * invalidate the layout the previous card just measured, forcing a full-page layout per
   * card (dozens on a collection page).
   */
  static #pendingCards = new Set();
  static #pendingFrame = 0;

  static #queueCardOverflow(swatches) {
    ColourVariantSwatches.#pendingCards.add(swatches);
    if (ColourVariantSwatches.#pendingFrame) return;
    ColourVariantSwatches.#pendingFrame = requestAnimationFrame(() => {
      ColourVariantSwatches.#pendingFrame = 0;
      const cards = [...ColourVariantSwatches.#pendingCards].filter((card) => card.isConnected && !card.#isExpanded);
      ColourVariantSwatches.#pendingCards.clear();

      const states = cards.map((card) => card.#resetCardOverflow());
      cards.forEach((card, i) => states[i] && card.#measureCardOverflow(states[i]));
      cards.forEach((card, i) => states[i] && card.#applyCardOverflow(states[i]));
    });
  }

  #resetCardOverflow() {
    const swatches = [
      ...this.querySelectorAll(".variant-option__swatch:not(.variant-option__swatch--overflow-item)"),
    ];
    const overflowLi = this.querySelector(".variant-option__swatch--overflow-item");
    const overflowCount = overflowLi?.querySelector(".colour-swatch-overflow__count");
    if (!swatches.length || !overflowLi || !overflowCount) return null;

    const totalSwatches = parseInt(this.dataset.totalSwatches ?? "0", 10) || swatches.length;
    // Swatches that exist but aren't rendered (beyond the server-side swatch_limit).
    const unrenderedCount = Math.max(0, totalSwatches - swatches.length);

    swatches.forEach((s) => s.classList.remove("hidden"));

    if (unrenderedCount > 0) {
      // Overflow is always needed — reveal it so its slot is included in the layout
      // measurement. The <li> has a fixed CSS width (--swatch-width) regardless
      // of text content, so a single-character placeholder gives the correct measurement.
      overflowLi.classList.remove("hidden");
      overflowCount.textContent = "+";
    } else {
      overflowLi.classList.add("hidden");
    }

    return { swatches, overflowLi, overflowCount, totalSwatches, unrenderedCount };
  }

  /** No DOM writes here, so every card's measurement shares one layout. */
  #measureCardOverflow(state) {
    const { swatches, overflowLi, unrenderedCount } = state;
    const firstRowTop = Math.round(swatches[0].getBoundingClientRect().top);

    state.swatchesOnRow1 = 0;
    for (const s of swatches) {
      if (Math.round(s.getBoundingClientRect().top) !== firstRowTop) break;
      state.swatchesOnRow1++;
    }

    state.overflowOnRow1 =
      unrenderedCount > 0 && Math.round(overflowLi.getBoundingClientRect().top) === firstRowTop;
  }

  #applyCardOverflow(state) {
    const { swatches, overflowLi, overflowCount, totalSwatches, unrenderedCount, swatchesOnRow1, overflowOnRow1 } =
      state;
    if (unrenderedCount === 0) {
      // All swatches are rendered — mirror the PDP path exactly.
      if (swatchesOnRow1 === swatches.length) return; // All fit; no overflow needed.

      const visibleCount = Math.max(1, swatchesOnRow1 - 1);
      swatches.forEach((s, i) => s.classList.toggle("hidden", i >= visibleCount));
      overflowCount.textContent = `+${swatches.length - visibleCount}`;
      overflowLi.classList.remove("hidden");
      return;
    }

    // unrenderedCount > 0: overflow is already visible and its row position is known.
    if (swatchesOnRow1 === swatches.length && overflowOnRow1) {
      // Everything fits in one row — just set the correct count.
      overflowCount.textContent = `+${unrenderedCount}`;
      return;
    }

    // Some items don't fit. Choose how many swatches to keep:
    // - overflow on row 1 → it already occupies its slot; keep only row-1 swatches.
    // - overflow on row 2 → free one swatch slot so it moves up to row 1.
    const visibleCount = overflowOnRow1 ? swatchesOnRow1 : Math.max(1, swatchesOnRow1 - 1);
    swatches.forEach((s, i) => s.classList.toggle("hidden", i >= visibleCount));
    overflowCount.textContent = `+${totalSwatches - visibleCount}`;
  }

  /**
   * Re-measure the single-row overflow when the element's WIDTH changes (PDP and card).
   * Ignores height-only changes (which our own show/hide causes, so this can't loop)
   * and coalesces resize bursts into one measure per frame (cards share one batch). Idle once expanded (PDP).
   */
  #startResizeObserver() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = new ResizeObserver((entries) => {
      if (this.#isExpanded) return;
      const width = Math.round(entries[0].contentRect.width);
      if (width === this.#lastWidth) return;
      this.#lastWidth = width;
      if (this.dataset.context === "card") {
        ColourVariantSwatches.#queueCardOverflow(this);
        return;
      }
      if (this.#rafId) return;
      this.#rafId = requestAnimationFrame(() => {
        this.#rafId = 0;
        this.#initPdpOverflow();
      });
    });
    this.#resizeObserver.observe(this);
  }

  /** Reveal all hidden swatches and hide the "+N" button (card + PDP overflow). */
  showAllSwatches(event) {
    event?.preventDefault();
    this.#isExpanded = true;
    this.querySelectorAll(".variant-option__swatch.hidden").forEach((s) => s.classList.remove("hidden"));
    this.querySelector(".variant-option__swatch--show-more")?.classList.add("hidden");
  }
}
if (!customElements.get("colour-variant-swatches")) {
  customElements.define("colour-variant-swatches", ColourVariantSwatches);
}
